import { supabase } from '@/lib/supabase';

const createEntity = (tableName) => ({
  filter: async (filters = {}, orderByArg, limitArg) => {
    let query = supabase.from(tableName).select('*');
    Object.entries(filters).forEach(([key, value]) => {
      query = query.eq(key, value);
    });
    // Support old Base44 signature: filter({...}, '-created_at', 10)
    if (typeof orderByArg === 'string') {
      const desc = orderByArg.startsWith('-');
      query = query.order(desc ? orderByArg.substring(1) : orderByArg, { ascending: !desc });
    }
    if (typeof limitArg === 'number') {
      query = query.limit(limitArg);
    }
    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  subscribe: (callback) => {
    const channel = supabase
      .channel(`realtime_${tableName}_${Date.now()}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: tableName },
        (payload) => callback([payload])
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  getById: async (id) => {
    const { data, error } = await supabase.from(tableName).select('*').eq('id', id).single();
    if (error) throw error;
    return data;
  },

  list: async (firstArg, secondArg) => {
    let query = supabase.from(tableName).select('*');
    let orderBy, ascending = false, limit, filters = {};

    // Support old Base44 signature: list('-created_date', 100)
    if (typeof firstArg === 'string') {
      const desc = firstArg.startsWith('-');
      orderBy = desc ? firstArg.substring(1) : firstArg;
      ascending = !desc;
      limit = secondArg;
    } else if (firstArg && typeof firstArg === 'object') {
      // New signature: list({ filters, orderBy, ascending, limit })
      filters = firstArg.filters || {};
      orderBy = firstArg.orderBy;
      ascending = firstArg.ascending ?? false;
      limit = firstArg.limit;
    }

    Object.entries(filters).forEach(([key, value]) => {
      query = query.eq(key, value);
    });
    if (orderBy) query = query.order(orderBy, { ascending });
    if (limit) query = query.limit(limit);
    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  create: async (record) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from(tableName)
      .insert({ ...record, user_id: user?.id })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  update: async (id, updates) => {
    const { data, error } = await supabase
      .from(tableName)
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  delete: async (id) => {
    const { error } = await supabase
      .from(tableName)
      .delete()
      .eq('id', id);
    if (error) throw error;
    return true;
  }
});

export const base44 = {
  entities: {
    Affiliation: createEntity('affiliations'),
    AdminLog: createEntity('admin_logs'),
    Challenge: createEntity('challenges'),
    ChallengeDraw: createEntity('challenge_draws'),
    CheckIn: createEntity('check_ins'),
    CourtDuel: createEntity('court_duels'),
    CourtEvent: createEntity('court_events'),
    CourtHighlight: createEntity('court_highlights'),
    CourtOwnership: createEntity('court_ownerships'),
    FCMToken: createEntity('fcm_tokens'),
    Phase: createEntity('phases'),
    PlayerStats: createEntity('player_stats'),
    Playground: createEntity('playgrounds'),
    RaffleEntry: createEntity('raffle_entries'),
    User: createEntity('users'),
    UserChallengeProgress: createEntity('user_challenge_progress'),
    XPEvents: createEntity('xp_events')
  },
  functions: {
    invoke: async (functionName, params) => {
      console.warn(`[Migration] base44.functions.invoke('${functionName}') appelé mais pas encore migré`, params);
      return { data: null, error: null };
    }
  }
};
